import { FIREWALL_REPLAY_MAX_SAMPLES } from '@nadoutong/contracts';
import type { FirewallReplaySample, FirewallReplaySampleContext } from '@nadoutong/contracts';

/**
 * Parse an operator-pasted corpus into replay samples (#1760, for #1759).
 *
 * The endpoint refuses an empty corpus, a non-string header value and more than
 * `FIREWALL_REPLAY_MAX_SAMPLES` samples. Those refusals are re-stated here so a
 * paste with one bad row is named locally, at the row, instead of coming back as
 * one undifferentiated `INVALID_PAYLOAD` for the whole batch — the corpora this
 * takes are hand-assembled and hundreds of rows long.
 *
 * `context` is a WHITELIST server-side: exactly four keys reach the engine and
 * anything else is dropped, because an open `derived` channel would let the
 * submitter invent a field a rule reads and steer its own verdict. We drop the
 * same keys here and report which, so the operator learns their field was
 * ignored rather than assuming it was honoured.
 */

export type FirewallReplayCorpusError =
  | { readonly kind: 'not_json' }
  | { readonly kind: 'not_array' }
  | { readonly kind: 'empty' }
  | { readonly kind: 'too_many'; readonly count: number }
  | { readonly kind: 'not_object'; readonly index: number }
  | { readonly kind: 'bad_header'; readonly index: number; readonly header: string };

export interface FirewallReplayCorpus {
  readonly samples: readonly FirewallReplaySample[];
  /** Context keys the whitelist rejected, in first-seen order. */
  readonly droppedContextKeys: readonly string[];
}

export type ParseFirewallReplayCorpusResult =
  | { readonly ok: true; readonly corpus: FirewallReplayCorpus }
  | { readonly ok: false; readonly error: FirewallReplayCorpusError };

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** The array of samples, from either a bare array or a `{ samples: [...] }` body. */
function samplesArray(parsed: unknown): readonly unknown[] | null {
  if (Array.isArray(parsed)) return parsed;
  if (isRecord(parsed) && Array.isArray(parsed.samples)) return parsed.samples;
  return null;
}

function parseHeaders(
  raw: unknown,
  index: number,
): { ok: true; headers: Record<string, string | readonly string[]> | null }
  | { ok: false; error: FirewallReplayCorpusError } {
  if (raw === undefined) return { ok: true, headers: null };
  if (!isRecord(raw)) return { ok: false, error: { kind: 'not_object', index } };
  const headers: Record<string, string | readonly string[]> = {};
  for (const [name, value] of Object.entries(raw)) {
    if (typeof value === 'string') {
      headers[name] = value;
      continue;
    }
    if (Array.isArray(value) && value.every((entry) => typeof entry === 'string')) {
      headers[name] = value.filter((entry): entry is string => typeof entry === 'string');
      continue;
    }
    return { ok: false, error: { kind: 'bad_header', index, header: name } };
  }
  return { ok: true, headers };
}

function parseContext(
  raw: unknown,
  dropped: string[],
): FirewallReplaySampleContext | null {
  if (!isRecord(raw)) return null;
  let context: FirewallReplaySampleContext = {};
  for (const [key, value] of Object.entries(raw)) {
    if ((key === 'client_format' || key === 'lane' || key === 'endpoint')
      && (typeof value === 'string' || value === null)) {
      context = { ...context, [key]: value };
      continue;
    }
    if (key === 'stream' && (typeof value === 'boolean' || value === null)) {
      context = { ...context, stream: value };
      continue;
    }
    if (!dropped.includes(key)) dropped.push(key);
  }
  return Object.keys(context).length === 0 ? null : context;
}

export function parseFirewallReplayCorpus(text: string): ParseFirewallReplayCorpusResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: { kind: 'not_json' } };
  }
  const rows = samplesArray(parsed);
  if (rows === null) return { ok: false, error: { kind: 'not_array' } };
  if (rows.length === 0) return { ok: false, error: { kind: 'empty' } };
  if (rows.length > FIREWALL_REPLAY_MAX_SAMPLES) {
    return { ok: false, error: { kind: 'too_many', count: rows.length } };
  }

  const droppedContextKeys: string[] = [];
  const samples: FirewallReplaySample[] = [];
  for (const [index, row] of rows.entries()) {
    if (!isRecord(row)) return { ok: false, error: { kind: 'not_object', index } };
    const headers = parseHeaders(row.headers, index);
    if (!headers.ok) return { ok: false, error: headers.error };
    const context = parseContext(row.context, droppedContextKeys);
    samples.push({
      ...(headers.headers === null ? {} : { headers: headers.headers }),
      ...(row.body === undefined ? {} : { body: row.body }),
      ...(context === null ? {} : { context }),
    });
  }
  return { ok: true, corpus: { samples, droppedContextKeys } };
}
